import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import { useNavigate } from '@plasma/ui.application.router';
import notification from '@plasma/ui.feedback.notification';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { PlantLayoutContext } from '../..';
import { apiUriCartDataHttp, apiUriConsumableDataHttp, apiUriDesignDrawingsHttp } from '../../services/conf/apiConf';
import { fetchAllDataGetFromAppFile_byApiName, fetchAllDataGet_byApiName } from '../../services/gets/getdata';
import { sendAllDataPost_byApiNameAndData, pollForFile } from '../../services/posts/postdata';
import linkExceptions from '../../services/Utils/linkExceptions';
import { CompanyConsumable } from '../../types/itemDocumentFInderMasterDatatype';
import translations from './i18n';
import { ItemDocumentFinderProps } from './item-document-finder';
import help from './item-document-finder.help';

function useItemDocumentFinderHook(props: ItemDocumentFinderProps) {
  useResourceBundle(translations, undefined, 'd5898e17-04aa-46df-be69-e52d9e2b8f0b');
  useHelpBundle(help);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const plantLayoutFromMemory = useContext(PlantLayoutContext);

  const [dataForColumns, setDataForColumns] = useState([]);
  const [search, setSearch] = useState('');
  const [nameSearch, setnameSearch] = useState('');

  const [clickedRow, setClickedRow] = useState('');
  const [showModal, setshowModal] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [selectedFirstPart, setSelectedFirstPart] = useState();
  const [selectedSecondPart, setSelectedSecondPart] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [quant, setQuant] = useState('');
  const [dataForModal, setDataForModal] = useState([]);
  const [companyConsumablesData, setCompanyConsumablesData] = useState<CompanyConsumable[]>([]);
  const [iFrameSrc, setIframeSrc] = useState('');

  const [showCodeModal, setshowCodeModal] = useState(false);
  const [codeModalTitle, setcodeModalTitle] = useState('');
  const [showSupplierInfoModal, setShowSupplierInfoModal] = useState(false);
  const [modalTitle, setmodalTitle] = useState('');
  const [gridQuantity, setGridQuantity] = useState([]);

  const [totalItems, settotalItems] = useState(0);
  const [lineseqData, setLineSeqData] = React.useState([]);
  const [equipNameSet, setEquipNameSet] = useState([]);
  const [consumeSet, setConsumeSet] = useState([]);
  const [zone, setZone] = useState([]);
  const [area, setArea] = useState([]);
  const [filtData, setFiltData] = useState([]);
  const [dataFromPlantLayout, setDataFromPlantLayout] = useState<any>([]);
  const [usedProfilesForCode, setusedProfilesForCode] = useState([]);
  const [usedEquipmentsForCode, setusedEquipmentsForCode] = useState([]);
  const [descriptionForCode, setdescriptionForCode] = useState('');
  const [viewDrawing, setviewDrawing] = useState(false);
  const [allGuidData, setallGuidData] = useState([]);

  useEffect(() => {
    fetchItemData();
    fetchInitialDataForPdf();
  }, []);

  const fetchItemData = async () => {
    try {
      // Execute all API calls,  Use Promise.all if you want them to run in parallel (faster)
      const [itemInShoppingCartToEdit, companyConsumablesData] = await Promise.all([
        fetchAllDataGet_byApiName('getItemInShoppingCartToEdit', apiUriCartDataHttp()),
        fetchAllDataGet_byApiName('GetCompanyConsumables?smsConsumableCode=', apiUriConsumableDataHttp()),
      ]);

      setDataForModal(itemInShoppingCartToEdit);
      setCompanyConsumablesData((companyConsumablesData as CompanyConsumable[]) ?? []);
      setDataFromPlantLayout(plantLayoutFromMemory?.layoutData ?? []);
    } catch (error) {
      console.error('Failed to Fetch Data:', error);
    }
  };

  const companyConsumablesBySmsCode = useMemo(() => {
    const bySmsCode = new Map<string, CompanyConsumable[]>();
    companyConsumablesData.forEach((item) => {
      const smsCode = item.smsConsumableCode?.trim();
      if (!smsCode) return;
      bySmsCode.set(smsCode, [...(bySmsCode.get(smsCode) ?? []), item]);
    });
    return bySmsCode;
  }, [companyConsumablesData]);

  // Table rows with the company code and description that belong to each row's SMS consumable code
  const tableRows = useMemo(
    () =>
      dataForColumns.map((row: any) => {
        const companyRows = companyConsumablesBySmsCode.get(String(row?.consumableCodes ?? '').trim()) ?? [];
        // The distinct values, comma-separated - or '-' when there is none (no company row, or blank values)
        const join = (values: (string | null | undefined)[]) =>
          Array.from(new Set(values.map((value) => value?.trim()).filter((value): value is string => !!value))).join(
            ', ',
          ) || 'N/A';
        return {
          ...row,
          companyConsumableCode: join(companyRows.map((item) => item.companyConsumableCode)),
          companyConsumableDesc: join(companyRows.map((item) => item.companyConsumableDesc)),
        };
      }),
    [dataForColumns, companyConsumablesBySmsCode],
  );

  const fetchInitialDataForPdf = () => {
    fetchAllDataGetFromAppFile_byApiName('GetConsumableItemDocumentGuid', apiUriDesignDrawingsHttp())
      .then((data) => {
        setallGuidData(data);
      })
      .catch((error) => {
        console.log('Failed to Fetch Data');
      });
  };

  const escapeSpecialCharacters = (str: string) => {
    return str.replace(/[_%]/g, (match) => `\\${match}`);
  };

  // The one getConsumableDetails call: every consumable matching the code / name search (no paging)
  const fetchConsumableDetails = async () => {
    try {
      const escapedSearch = encodeURIComponent(escapeSpecialCharacters(search));
      const escapedNameSearch = encodeURIComponent(escapeSpecialCharacters(nameSearch));
      const response = await fetchAllDataGet_byApiName(
        `getConsumableDetails?consumableCode=${escapedSearch}&consumableDescription=${escapedNameSearch}`,
        apiUriConsumableDataHttp(),
      );
      setDataForColumns(response?.items ?? []);
      settotalItems(response?.totalElement ?? 0);
    } catch (error) {
      console.log('failed to fetch', error);
    }
  };

  // Fetch once on opening the page and once per search change
  useEffect(() => {
    fetchConsumableDetails();
  }, [search, nameSearch]);

  const handleGoHome = () => {
    navigate('/home-screen');
  };

  const handleSelection = (selectedRowIndex, selectedRowData, event) => {
    if (
      selectedRowData.consumableCodes === event.target.textContent &&
      event.target.tagName.toLowerCase() === 'div' &&
      !linkExceptions.includes(event.target.textContent)
    ) {
      console.log(selectedRowData.consumableName);
      handleConsumableCodeClick(selectedRowData.consumableName, selectedRowData.consumableCodes);
    } else {
      setshowModal(true);

      setClickedRow(selectedRowData.consumableCodes);
    }

    //console.log(selectedRowIndex, selectedRowData, event.target.textContent);
    //console.log(selectedRowData.consumableCodes);
  };

  const dropdownArr = [];
  const dropQuant = [];
  const equipname = [];
  const lineSequence = [];
  const consumableName = [];
  const areaArr = [];
  const zoneArr = [];

  useEffect(() => {
    dataForModal.filter((data) => {
      if (data?.itemCode === clickedRow) {
        lineSequence.push(data.lineseq);
        dropdownArr.push(`${data.itemCode}-${data.consumableName}`);
        dropQuant.push(data.itemcount);
        equipname.push(data.equipmentName);
        consumableName.push(data.consumableName);
        areaArr.push(data.areaName);
        zoneArr.push(data.zoneName);
      }
    });
    setLineSeqData(lineSequence);
    setGridQuantity(dropQuant);
    setEquipNameSet(equipname);
    setDropdownOptions(dropdownArr);
    setConsumeSet(consumableName);
    setArea(areaArr);
    setZone(zoneArr);

    const filteredData = dataForModal.filter((item) => item.itemcode === clickedRow);

    setFiltData(filteredData);
  }, [clickedRow]);

  const handleRowClick = (rowIndex, rowData, event) => {
    setClickedRow(rowData.consumableCodes);
    setshowModal(true);
    setshowCodeModal(true);
  };

  const HandleModalCancel = () => {
    setIframeSrc('');
    setshowModal(false);
    setQuant('');
    setSelectedValue('');
    setClickedRow('');
    setFiltData([]);
  };

  const HandleCodeModalCancel = () => {
    setshowCodeModal(false);
  };

  const handleDropdownChange = (event) => {
    setSelectedValue(event.target.value);
    const parts = event.target.value?.split(':');
    //console.log('Selected value:', event.target.value);
    if (parts) {
      const firstPart = parts[0]?.trim();
      const firstPartfinal = parseInt(firstPart, 10);
      const secondPart = parts[1]?.trim();

      setSelectedFirstPart(firstPartfinal);
      setSelectedSecondPart(secondPart);
    }
  };

  const handleUp = (index, newQuantity) => {
    const updatedGridQuantity = [...gridQuantity];
    updatedGridQuantity[index] = newQuantity;

    setGridQuantity(updatedGridQuantity);

    const updatedData = filtData.map((oneItem, i) => ({
      ...oneItem,
      itemcount: i === index ? newQuantity : oneItem.itemcount, // Update itemcount at the specified index
    }));
    setFiltData(updatedData);
  };

  const handleDown = (index, newQuantity) => {
    const actualQuantity = newQuantity - 1;
    //console.log('quantity: ',actualQuantity);
    //  filtData[index].itemcount=newQuantity;
    //  gridQuantity[index].itemcount=newQuantity;

    const updatedGridQuantity = [...gridQuantity];
    if (actualQuantity > 0) {
      updatedGridQuantity[index] = actualQuantity;
    } else if (actualQuantity === 0) {
      updatedGridQuantity[index] = 0;
    }

    //console.log(updatedGridQuantity);

    setGridQuantity(updatedGridQuantity);

    const updatedData = filtData.map((item, i) => ({
      ...item,
      itemcount: i === index ? (newQuantity === 0 ? 0 : actualQuantity) : item.itemcount, // Update itemcount at the specified index
    }));
    setFiltData(updatedData);
  };

  const handleOk = () => {
    sendAllDataPost_byApiNameAndData('PostItemInShoppingCartConsumableUtilization', filtData, apiUriCartDataHttp())
      .then((data) => {
        //console.log('response data', data);
        fetchConsumableDetails();

        fetchAllDataGet_byApiName('getItemInShoppingCartToEdit', apiUriCartDataHttp())
          .then((data) => setDataForModal(data))
          .catch((error) => {
            console.log('failed to fetch');
          });
      })
      .catch((error) => {
        console.log(error);
      });

    HandleModalCancel();
  };

  const handleConsumableCodeClick = (name, code) => {
    //console.log('Consumable code: ',code);
    if (dataFromPlantLayout && dataFromPlantLayout.length > 0) {
      const profilesForCode = dataFromPlantLayout
        ?.map((onedataFromPlantLayout) => {
          const filteredProfiles = onedataFromPlantLayout.consumables
            ?.filter((oneData) => oneData.consumablecodeext === code)
            ?.map((oneItem) => oneItem.productprofiles);
          return filteredProfiles;
        })
        ?.filter((item_loc) => item_loc?.length > 0);

      // const nameForCode = dataFromPlantLayout
      //   ?.map((onedataFromPlantLayout) => {
      //     const filteredProfiles = onedataFromPlantLayout.consumables?.filter(
      //       (oneData) => oneData.consumablecodeext === code,
      //     );
      //     return filteredProfiles;
      //   })
      //   ?.filter((item_loc) => item_loc?.length > 0);

      const equipmentsForCode = dataFromPlantLayout
        ?.map((onedataFromPlantLayout) => {
          const filteredCode = onedataFromPlantLayout.consumables?.filter(
            (oneData) => oneData.consumablecodeext === code,
          );
          if (filteredCode && filteredCode.length > 0) {
            return onedataFromPlantLayout;
          }
        })
        ?.filter((item) => item !== undefined && item !== null);

      //console.log(nameForCode[0][0]);

      setdescriptionForCode(name);
      setusedProfilesForCode(profilesForCode[0][0]);
      setusedEquipmentsForCode(equipmentsForCode);
      setshowCodeModal(true);
      setcodeModalTitle(code);
    }
  };

  async function fetchAndSetIframeSource(guId: string) {
    const queryParams = new URLSearchParams({
      guid: guId,
      // Add more query parameters as needed
    });

    try {
      const blob: any = await pollForFile(
        `getConsumableItemDesign?${queryParams}`,
        {},
        undefined,
        undefined,
        apiUriDesignDrawingsHttp(),
      );
      //console.log(blob);
      const blobUrl = URL.createObjectURL(blob);
      setIframeSrc(blobUrl);
    } catch (error) {
      console.log(error);
      notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 3);
    }
  }

  const handleDrawingModal = () => {
    const guidForSelectedCode: any = allGuidData?.find((item: any) => item.docfor === codeModalTitle);
    fetchAndSetIframeSource(guidForSelectedCode.guid[0]);
    setviewDrawing(true);
  };

  const handleDrawingModalCancel = () => {
    setIframeSrc('');
    setviewDrawing(false);
  };

  const handleSupplierInfoModal = () => {
    setShowSupplierInfoModal(true);
  };

  const handleSupplierInfoModalCancel = () => {
    setShowSupplierInfoModal(false);
  };

  const dataToSend = [
    {
      itemtype: '',
      itemcode: clickedRow,
      itemcount: quant,
      totalItemsCount: 0,
      itemcost: 0,
      currency: '',
      productcode: selectedSecondPart,
      lineseq: selectedFirstPart,
      displayseq: 0,
      productType: '',
      productDescription: '',
      equipmentCode: '',
      equipmentName: '',
      consumableName: '',
    },
  ];

  return {
    t,
    dataForColumns: tableRows,
    handleGoHome,
    search,
    nameSearch,
    setSearch,
    setnameSearch,
    clickedRow,
    setClickedRow,
    showModal,
    setshowModal,
    handleSelection,
    handleRowClick,
    HandleModalCancel,
    dropdownOptions,
    handleDropdownChange,
    selectedValue,
    quant,
    handleOk,
    setQuant,
    HandleCodeModalCancel,
    setcodeModalTitle,
    setmodalTitle,
    gridQuantity,
    totalItems,
    setGridQuantity,
    showCodeModal,
    equipNameSet,
    consumeSet,
    area,
    zone,
    lineseqData,
    filtData,
    handleUp,
    handleDown,
    handleConsumableCodeClick,
    codeModalTitle,
    descriptionForCode,
    usedEquipmentsForCode,
    usedProfilesForCode,
    viewDrawing,
    iFrameSrc,
    handleDrawingModal,
    handleDrawingModalCancel,
    handleSupplierInfoModal,
    handleSupplierInfoModalCancel,
    showSupplierInfoModal,
  };
}

export default useItemDocumentFinderHook;
